<template>
  <div class="segment-library">
    <div class="segment-library-header">
      <h3 class="segment-library-title" v-if="!compact">Saved Segment Library</h3>
      <div v-if="!compact" class="segment-library-controls">
        <div class="segment-library-chips">
          <button
            v-for="chip in chips"
            :key="chip.value"
            type="button"
            class="segment-chip"
            :class="{ 'segment-chip-active': activeChip === chip.value }"
            @click="activeChip = chip.value">{{ chip.label }}</button>
        </div>
        <input
          type="text"
          class="form-control segment-library-search"
          v-model="search"
          placeholder="Search by name, journey, customer, country..." />
        <nv-select v-if="folderNames.length" class="form-control segment-folder-filter" v-model="folderFilter">
          <option value="">All folders</option>
          <option v-for="name in folderNames" :key="name" :value="name">{{ name }}</option>
        </nv-select>
        <div class="segment-view-toggle">
          <button type="button" class="segment-view-btn" :class="{ active: viewMode === 'list' }" title="List view" @click="viewMode = 'list'"><i class="fa fa-list"></i></button>
          <button type="button" class="segment-view-btn" :class="{ active: viewMode === 'card' }" title="Card view" @click="viewMode = 'card'"><i class="fa fa-th-large"></i></button>
        </div>
      </div>
    </div>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>

    <div v-if="!loading && !segments.length" class="segment-library-empty">No saved segments yet.</div>
    <div v-else-if="!loading && !filteredSegments.length" class="segment-library-empty">
      No segments match your filters.
      <a href="#" @click.prevent="clearFilters">Clear filters</a>
    </div>

    <table v-if="showList && visibleSegments.length" class="table segment-list-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Type</th>
          <th>Journey</th>
          <th>Customer</th>
          <th>Market</th>
          <th>Users</th>
          <th>Folder</th>
          <th>Created</th>
          <th>Updated</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="segment in visibleSegments" :key="segment.id">
          <td class="segment-list-name">{{ segment.queryName }}</td>
          <td><span class="segment-card-type-badge" :class="typeBadgeClass(segment)">{{ typeLabel(segment) }}</span></td>
          <td>{{ journeyFor(segment) || '—' }}</td>
          <td>{{ (segment.criteria && segment.criteria.customer) || '—' }}</td>
          <td>{{ segment.countryName || '—' }}</td>
          <td>{{ userCount(segment) }}</td>
          <td>{{ segment.folderName || '—' }}</td>
          <td>{{ formatDate(segment.createdAt) }}</td>
          <td>{{ formatDate(segment.updatedAt) }}</td>
          <td class="segment-list-actions">
            <router-link :to="editLink(segment)" class="btn btn-sm btn-default"><i class="fa fa-pencil"></i> Edit</router-link>
            <router-link v-if="copyLink(segment)" :to="copyLink(segment)" class="btn btn-sm btn-default"><i class="fa fa-copy"></i> Copy</router-link>
            <router-link to="/opportunity" class="btn btn-sm btn-primary">Use in campaign</router-link>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="!showList" class="segment-library-grid">
      <div v-for="segment in visibleSegments" :key="segment.id" class="segment-card">
        <div class="segment-card-header">
          <span class="segment-card-name">{{ segment.queryName }}</span>
          <span class="segment-card-type-badge" :class="typeBadgeClass(segment)">{{ typeLabel(segment) }}</span>
        </div>
        <div class="segment-card-description">{{ describe(segment) }}</div>
        <div class="segment-card-tags" v-if="tagsFor(segment).length">
          <span class="segment-card-tag" v-for="tag in tagsFor(segment)" :key="tag">{{ tag }}</span>
        </div>
        <div class="segment-card-meta">
          <span title="User count"><i class="fa fa-users"></i> {{ userCount(segment) }}</span>
          <span v-if="journeyFor(segment)" title="Journey"><i class="fa fa-sitemap"></i> {{ journeyFor(segment) }}</span>
          <span title="Market"><i class="fa fa-globe"></i> {{ segment.countryName || '—' }}</span>
        </div>
        <div class="segment-card-actions">
          <router-link :to="editLink(segment)" class="btn btn-sm btn-default"><i class="fa fa-pencil"></i> Edit</router-link>
          <router-link v-if="copyLink(segment)" :to="copyLink(segment)" class="btn btn-sm btn-default"><i class="fa fa-copy"></i> Copy</router-link>
          <router-link to="/opportunity" class="btn btn-sm btn-primary">Use in campaign</router-link>
        </div>
      </div>
    </div>

    <div v-if="!compact && filteredSegments.length" class="segment-pagination">
      <span class="segment-pagination-info">{{ rangeStart }}–{{ rangeEnd }} of {{ filteredSegments.length }}</span>
      <nv-select class="form-control segment-page-size" v-model.number="pageSize">
        <option v-for="n in pageSizes" :key="n" :value="n">{{ n }} / page</option>
      </nv-select>
      <button type="button" class="btn btn-sm btn-default" :disabled="page <= 1" @click="page--">Prev</button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button type="button" class="btn btn-sm btn-default" :disabled="page >= totalPages" @click="page++">Next</button>
    </div>
  </div>
</template>

<script>
import api from '../../../../utils/latestApi'
import { getEnv } from '../../../../utils/utilities'
import * as helpers from '../listQueries/helpers'
import { buildCriteriaDescription, buildCriteriaTags } from './helpers'

export default {
  name: 'SegmentLibrary',
  props: {
    compact: {
      type: Boolean,
      default: false
    },
    refreshToken: {
      type: [Number, String],
      default: 0
    }
  },
  data () {
    return {
      loading: false,
      env: '',
      segments: [],
      activeChip: 'all',
      search: '',
      folderFilter: '',
      viewMode: 'list',
      page: 1,
      pageSize: 10,
      pageSizes: [10, 25, 50],
      chips: [
        { value: 'all', label: 'All' },
        { value: 'ai', label: 'AI-built' },
        { value: 'uploaded', label: 'Uploaded' },
        { value: 'query', label: 'Query based' }
      ]
    }
  },
  computed: {
    sortedSegments () {
      return [...this.segments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    folderNames () {
      return [...new Set(this.segments.map(s => s.folderName).filter(Boolean))].sort()
    },
    filteredSegments () {
      let list = this.sortedSegments
      if (this.folderFilter) list = list.filter(s => s.folderName === this.folderFilter)
      if (this.activeChip === 'ai') list = list.filter(s => s.queryType === 'AI')
      else if (this.activeChip === 'uploaded') list = list.filter(s => s.queryType === 'Custom')
      else if (this.activeChip === 'query') list = list.filter(s => s.queryType !== 'AI' && s.queryType !== 'Custom')

      const term = this.search.trim().toLowerCase()
      if (term) {
        list = list.filter(s => {
          const haystack = [
            s.queryName,
            s.countryName,
            s.criteria && s.criteria.journeyLabel,
            s.criteria && s.criteria.customer
          ].filter(Boolean).join(' ').toLowerCase()
          return haystack.includes(term)
        })
      }
      return list
    },
    visibleSegments () {
      // Only the compact embedded widget shows a capped preview; the full Library tab lists everything.
      if (this.compact) {
        return this.filteredSegments.slice(0, 6)
      }
      const start = (this.page - 1) * this.pageSize
      return this.filteredSegments.slice(start, start + this.pageSize)
    },
    showList () {
      return !this.compact && this.viewMode === 'list'
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.filteredSegments.length / this.pageSize))
    },
    rangeStart () {
      return this.filteredSegments.length ? (this.page - 1) * this.pageSize + 1 : 0
    },
    rangeEnd () {
      return Math.min(this.page * this.pageSize, this.filteredSegments.length)
    }
  },
  watch: {
    activeChip () { this.page = 1 },
    search () { this.page = 1 },
    folderFilter () { this.page = 1 },
    pageSize () { this.page = 1 },
    filteredSegments () {
      if (this.page > this.totalPages) this.page = this.totalPages
    },
    refreshToken () {
      this.fetchSegments()
    }
  },
  async mounted () {
    this.env = await getEnv()
    await this.fetchSegments()
  },
  methods: {
    async fetchSegments () {
      this.loading = true
      try {
        const response = await api.get(`audience-builder/${this.env.env}/queries`, { baseURL: this.env.baseURL })
        this.segments = helpers.formatQueriesRawData(response.data.data)
        this.$emit('count-changed', this.segments.length)
      } catch (error) {
        this.$swal({
          title: 'Failure',
          type: 'error',
          text: error.response ? error.response.data.payload.message : 'An error occurred while loading segments'
        })
      } finally {
        this.loading = false
      }
    },
    formatDate (value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d)) return '—'
      // Local calendar date (not toISOString) so IST users don't see the day shift.
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    clearFilters () {
      this.activeChip = 'all'
      this.search = ''
      this.folderFilter = ''
    },
    // Copy reuses the existing copyAudience flow of the uploaded / query-based editors (the AI builder has none).
    copyLink (segment) {
      if (segment.queryType === 'AI') return null
      if (segment.queryType === 'Custom') return `/audience-builder/upload?queryId=${segment.id}&action=copyAudience`
      return `/audience-builder/update?queryId=${segment.id}&action=copyAudience`
    },
    typeLabel (segment) {
      if (segment.queryType === 'AI') return 'AI-built'
      if (segment.queryType === 'Custom') return 'Uploaded'
      return 'Query based'
    },
    typeBadgeClass (segment) {
      if (segment.queryType === 'AI') return 'segment-badge-ai'
      if (segment.queryType === 'Custom') return 'segment-badge-uploaded'
      return 'segment-badge-manual'
    },
    describe (segment) {
      return buildCriteriaDescription(segment.criteria || {})
    },
    tagsFor (segment) {
      return buildCriteriaTags(segment.criteria || {})
    },
    userCount (segment) {
      const count = segment.criteria && segment.criteria.savedUserCount
      return count !== undefined && count !== null ? count : '—'
    },
    journeyFor (segment) {
      return segment.criteria && segment.criteria.journeyLabel
    },
    useLink (segment) {
      if (segment.queryType === 'AI') return `/audience-builder/ai-generate?queryId=${segment.id}`
      if (segment.queryType === 'Custom') return `/audience-builder/upload?queryId=${segment.id}`
      return `/audience-builder/update?queryId=${segment.id}`
    },
    // Opens the segment in its builder pre-filled for editing. AI segments reopen in the AI builder
    // (?queryId → form.vue loadSegmentForEdit); uploaded/filter segments reuse their own editors.
    editLink (segment) {
      return this.useLink(segment)
    }
  }
}
</script>

<style scoped>
.segment-library {
  margin-top: 20px;
}
.segment-library-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}
.segment-library-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a0aec0;
}
.segment-library-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.segment-chip {
  margin-right: 4px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #4a5568;
  border-radius: 999px;
  padding: 4px 14px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.segment-chip:hover {
  border-color: #7DD3C8;
}
.segment-chip-active {
  background: #0D9488;
  border-color: #0D9488;
  color: #fff;
}
.segment-folder-filter {
  min-width: 180px;
  display: inline-block;
  vertical-align: middle;
}
.segment-library-search {
  width: 280px;
  display: inline-block;
  border-radius: 999px;
}
.segment-view-toggle {
  display: inline-flex;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}
.segment-view-btn {
  border: 0;
  background: #fff;
  color: #4a5568;
  padding: 5px 12px;
  cursor: pointer;
}
.segment-view-btn.active {
  background: #0D9488;
  color: #fff;
}
.segment-list-table {
  background: #fff;
  font-size: 13px;
}
.segment-list-name {
  font-weight: 600;
  word-break: break-word;
}
.segment-list-actions {
  white-space: nowrap;
  text-align: right;
}
.segment-list-actions .btn {
  margin-left: 6px;
  border-radius: 999px;
}
.segment-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
  font-size: 12px;
  color: #4a5568;
}
.segment-page-size {
  min-width: 120px;
  display: inline-block;
  vertical-align: middle;
}
.segment-library-empty {
  color: #999;
  padding: 20px 0;
}
.segment-library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}
.segment-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}
.segment-card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}
.segment-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.segment-card-name {
  font-weight: 600;
  word-break: break-word;
}
.segment-card-type-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.segment-badge-ai {
  background: #e6fffa;
  color: #234e52;
}
.segment-badge-uploaded {
  background: #E6F5F4;
  color: #1E3A5F;
}
.segment-badge-manual {
  background: #f0f0f0;
  color: #4a5568;
}
.segment-card-description {
  font-size: 13px;
  color: #4a5568;
}
.segment-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.segment-card-tag {
  font-size: 11px;
  background: #fff5f5;
  color: #c53030;
  border-radius: 999px;
  padding: 2px 9px;
}
.segment-card-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #718096;
  flex-wrap: wrap;
}
.segment-card-actions {
  margin-top: 4px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.segment-card-actions .btn {
  border-radius: 999px;
  padding: 4px 16px;
}
</style>



// WEBPACK FOOTER //
// SegmentLibrary.vue?8fd614b2